import React, { Fragment } from 'react';
import { map } from 'lodash';
import PropTypes from 'prop-types';
import isValid from 'date-fns/is_valid';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import {
	compose,
	defaultProps,
	withHandlers,
	withPropsOnChange,
} from 'recompose';

import defaultUnitFilters from 'config/filters';

import { Button } from 'components/core/Button';
import { Link } from 'components/core/Link';
import { Datepicker } from 'components/core/Datepicker';
import Remove from 'components/svg/Remove';

import FilterGroup from './FilterGroup';
import './FiltersList.scss';

export const enhance = compose(
	defaultProps({ unitFilters: defaultUnitFilters, storageTypeUrls: {} }),
	withHandlers({
		onMoveInDateChange:
			({ onFilter, delayFiltering }) =>
			(event) => {
				onFilter(
					{ moveInDate: event.target.value.string },
					delayFiltering,
				);
			},
		onClearMoveInDate:
			({ onFilter, delayFiltering }) =>
			() => {
				onFilter({ moveInDate: null }, delayFiltering);
			},
	}),
	withPropsOnChange(
		['searchParams', 'searchType'],
		({ searchParams, searchType, unitFilters, storageTypeUrls }) => {
			let searchToggleText = 'Self-Storage';
			if (searchType !== 'vehicle') {
				const isCarClp = /car-storage$/i.test(storageTypeUrls.vehicle);
				searchToggleText = isCarClp ? 'Car Storage' : 'Vehicle Storage';
			}
			return {
				moveInDate: isValid(parse(searchParams.moveInDate))
					? format(parse(searchParams.moveInDate), 'MMM D, YYYY')
					: '',
				filters: unitFilters[searchType],
				searchToggleText,
				searchToggleLink:
					searchType === 'vehicle'
						? storageTypeUrls.storage
						: storageTypeUrls.vehicle,
			};
		},
	),
);

/**
 * This is a collection of filter types, including a date picker, and
 * collapsable groups of radio and checkbox inputs.
 *
 * The listKey prop is required where more than one instance of FiltersList is
 * used on a page to namespace the id's of the created inputs.
 */

export function FiltersList({
	filters,
	moveInDate,
	onClearMoveInDate,
	onMoveInDateChange,
	searchParams,
	searchToggleLink,
	searchToggleText,
	onFilter,
	onShowModal,
	delayFiltering,
	showSearchTypeLink,
	listKey,
	facetCounts,
	useNewSearch,
}) {
	return (
		<Fragment>
			{useNewSearch && (
				<div className="datepicker-filter">
					<Datepicker
						label="Planned Start Date:"
						name="moveInDate"
						placeholder="Select Date"
						withIcon
						value={moveInDate}
						onChange={onMoveInDateChange}
						id="search-filter-mid"
					/>
					{!!moveInDate && (
						<Button
							className="clear-field"
							onClick={onClearMoveInDate}
						>
							<Remove />
						</Button>
					)}
				</div>
			)}
			<div className="filter-lists">
				{map(filters, (filter, key) => (
					<FilterGroup
						facetCounts={facetCounts}
						listKey={listKey}
						key={key}
						filterName={key}
						allowMultiple={filter.multiple}
						label={filter.label}
						options={filter.options}
						searchParams={searchParams}
						onFilter={onFilter}
						delayFiltering={delayFiltering}
						filterType={filter.type}
						showing={filter.default}
						modal={filter.modal}
						onShowModal={onShowModal}
						useNewSearch={useNewSearch}
					/>
				))}
			</div>
			{showSearchTypeLink && (
				<Link
					href={searchToggleLink}
					className="search-toggle-link"
				>
					View {searchToggleText}
				</Link>
			)}
			{!useNewSearch && (
				<div className="datepicker-filter">
					<Datepicker
						label="Planned Start Date:"
						name="moveInDate"
						placeholder="Select Date"
						withIcon
						value={moveInDate}
						onChange={onMoveInDateChange}
						id="search-filter-mid"
					/>
					{!!moveInDate && (
						<Button
							className="clear-field"
							onClick={onClearMoveInDate}
						>
							<Remove />
						</Button>
					)}
				</div>
			)}
		</Fragment>
	);
}

FiltersList.propTypes = {
	listKey: PropTypes.string,
	filters: PropTypes.object,
	onClearMoveInDate: PropTypes.func.isRequired,
	onFilter: PropTypes.func.isRequired,
	delayFiltering: PropTypes.bool,
	onShowModal: PropTypes.func,
	onMoveInDateChange: PropTypes.func.isRequired,
	moveInDate: PropTypes.string,
	searchParams: PropTypes.object.isRequired,
	searchToggleLink: PropTypes.string.isRequired,
	searchToggleText: PropTypes.string.isRequired,
	showSearchTypeLink: PropTypes.bool,
	facetCounts: PropTypes.object,
	useNewSearch: PropTypes.bool,
};

export default enhance(FiltersList);
